import { useCallback, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Papa from "papaparse";
import { toast } from "react-hot-toast";
import useDownloader from "react-use-downloader";

import {
  downloadInvitationCsvTemplate,
  inviteUsersByCSVFile,
} from "../../../../../../services/apiUser";
import {
  validateInvitationCsvHeaders,
  validateInvitationCsvRows,
} from "../utils/uploadUserCSVUtils";

function getUploadCsvSuccessMessage(response) {
  if (typeof response?.data?.message === "string" && response.data.message.trim()) {
    return response.data.message;
  }

  return "Bulk invitation processed successfully.";
}

function getUploadCsvErrorMessage(error) {
  const responseData = error?.response?.data;
  const failedRowMessage = error?.response?.data?.data?.responseData?.failed_rows
    ?.map((row) => row?.error)
    .find((message) => typeof message === "string" && message.trim());

  if (failedRowMessage) {
    return failedRowMessage;
  }

  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message;
  }

  if (typeof responseData?.message?.message === "string" && responseData.message.message.trim()) {
    return responseData.message.message;
  }

  if (typeof responseData?.error?.message === "string" && responseData.error.message.trim()) {
    return responseData.error.message;
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }

  return "Something went wrong!";
}

export function useUploadUserCSV({ onClose }) {
  const queryClient = useQueryClient();
  const [openImportUsers, setOpenImportUsers] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const { download } = useDownloader();

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setErrorMessage("");
    setPreviewRows([]);
    setRawRows([]);
    setOpenImportUsers(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  const handleImportUsersClose = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleUploadSuccess = useCallback((uploadResponse) => {
    const failedRowMessage = uploadResponse?.data?.responseData?.failed_rows
      ?.map((row) => row?.error)
      .find((message) => typeof message === "string" && message.trim());

    if (failedRowMessage) {
      toast.error(failedRowMessage);
      handleClose();
      return;
    }

    const successMessage = getUploadCsvSuccessMessage(uploadResponse);

    setErrorMessage("");
    toast.success(successMessage);
    handleClose();
  }, [handleClose]);

  const { mutate: submitFileMutate, isPending: isSubmittingFile } = useMutation({
    mutationFn: async (payload) => {
      toast.dismiss();

      return await toast.promise(inviteUsersByCSVFile(payload), {
        loading: "Processing...",
        success: () => "",
        error: (error) => getUploadCsvErrorMessage(error),
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ["LDAPinternalList"],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      handleUploadSuccess(response);
    },
    onError: (error) => {
      handleClose();
    },
  });

  const parseCSVFile = useCallback((file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete(results) {
        if (!results?.data?.length) {
          setErrorMessage("The CSV file is empty or contains no valid data.");
          setPreviewRows([]);
          setRawRows([]);
          return;
        }

        const headerValidationError = validateInvitationCsvHeaders(
          results.meta?.fields || []
        );

        if (headerValidationError) {
          setErrorMessage(headerValidationError);
          setPreviewRows([]);
          setRawRows([]);
          return;
        }

        const validatedResult = validateInvitationCsvRows(results.data);

        setErrorMessage("");
        setSelectedFile(file);
        setRawRows(results.data);
        setPreviewRows(validatedResult.rows);
        setOpenImportUsers(true);
      },
      error() {
        setErrorMessage("Unable to read the CSV file.");
        setPreviewRows([]);
        setRawRows([]);
      },
    });
  }, []);

  const handleFiles = useCallback(
    (files) => {
      const file = files?.[0];

      if (!file) {
        setErrorMessage("Please upload a CSV file.");
        return;
      }

      parseCSVFile(file);
    },
    [parseCSVFile]
  );

  const handleTemplateDownload = useCallback(() => {
    downloadInvitationCsvTemplate()
      .then((response) => {
        const blob = response?.data;
        const url = URL.createObjectURL(blob);
        download(url, "invitation_users_template.csv");
      })
      .catch(() => {
        setErrorMessage("Unable to download invitation template.");
      });
  }, [download]);

  const submitFileUpload = useCallback(() => {
    if (!selectedFile || !previewRows.length) {
      setErrorMessage("Please upload a valid CSV file.");
      return;
    }

    const { isValid } = validateInvitationCsvRows(rawRows);

    if (!isValid) {
      setErrorMessage("Please fix the invalid rows before submitting.");
      return;
    }

    setErrorMessage("");
    submitFileMutate({ file: selectedFile });
  }, [previewRows.length, rawRows, selectedFile, submitFileMutate]);

  const hasInvalidRows = useMemo(
    () => previewRows.some((row) => !row.isValid),
    [previewRows]
  );

  return {
    errorMessage,
    handleClose,
    handleFiles,
    handleTemplateDownload,
    handleImportUsersClose,
    hasInvalidRows,
    isSubmitting: isSubmittingFile,
    openImportUsers,
    previewRows,
    selectedFile,
    submitFileUpload,
  };
}
